import { Badge, Button, Table } from '@vwfs-bronson/bronson-react';
import { Agent, Schedule } from '../../fes';
import { RowActionsMenu } from '../RowActionsMenu';
import { formatInTimeZone } from './cronPreview';

const lastRunLabels: Record<string, string> = {
  DISPATCHED: 'Started',
  SUCCEEDED: 'Succeeded',
  SKIPPED: 'Skipped',
  FAILED: 'Failed',
};

function describeTarget(schedule: Schedule, agentsById: Map<string, Agent>): string {
  const agentId = schedule.jobTemplate?.agentId;
  if (!agentId) {
    return 'No agent';
  }
  return agentsById.get(agentId)?.name ?? agentId;
}

function describeNextRun(schedule: Schedule): string {
  if (!schedule.enabled) {
    return 'Paused';
  }
  if (!schedule.nextRunAt) {
    return '-';
  }
  return formatInTimeZone(new Date(schedule.nextRunAt), schedule.trigger?.timezone ?? 'UTC');
}

function describeLastRun(schedule: Schedule): string {
  if (!schedule.lastRunAt) {
    return 'Never run';
  }

  const when = formatInTimeZone(new Date(schedule.lastRunAt), schedule.trigger?.timezone ?? 'UTC');
  const outcome = lastRunLabels[String(schedule.lastRunStatus)] ?? String(schedule.lastRunStatus ?? '');

  return outcome ? `${outcome} · ${when}` : when;
}

type SchedulesTableProps = {
  schedules: Schedule[];
  agentsById: Map<string, Agent>;
  busyScheduleId?: string;
  onEdit: (schedule: Schedule) => void;
  onToggleEnabled: (schedule: Schedule) => void;
  onRunNow: (schedule: Schedule) => void;
  onDelete: (schedule: Schedule) => void;
  onShowRuns: (schedule: Schedule) => void;
};

export function SchedulesTable({
  schedules,
  agentsById,
  busyScheduleId,
  onEdit,
  onToggleEnabled,
  onRunNow,
  onDelete,
  onShowRuns,
}: SchedulesTableProps) {
  if (schedules.length === 0) {
    return <p className="app-empty">No schedules yet. Create one to run an agent on a recurring basis.</p>;
  }

  return (
    <div className="app-table-wrap">
      <Table className="app-table" colored>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Runs</Table.Th>
            <Table.Th>Schedule</Table.Th>
            <Table.Th>Next run</Table.Th>
            <Table.Th>Last run</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th className="app-table-actions-column app-table-actions-column--wide">Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {schedules.map(schedule => {
            const isBusy = busyScheduleId === schedule.scheduleId;

            return (
              <Table.Tr key={schedule.scheduleId}>
                <Table.Td>
                  <strong>{schedule.name}</strong>
                  {schedule.description ? (
                    <>
                      <br />
                      <small>{schedule.description}</small>
                    </>
                  ) : null}
                </Table.Td>
                <Table.Td>{describeTarget(schedule, agentsById)}</Table.Td>
                <Table.Td>
                  <code>{schedule.trigger?.cron ?? '-'}</code>
                  <br />
                  <small>{schedule.trigger?.timezone ?? '-'}</small>
                </Table.Td>
                <Table.Td>{describeNextRun(schedule)}</Table.Td>
                <Table.Td title={schedule.lastError}>{describeLastRun(schedule)}</Table.Td>
                <Table.Td>
                  <Badge
                    label={schedule.enabled ? 'Active' : 'Paused'}
                    modifier={schedule.enabled ? 'success' : undefined}
                  />
                  {(schedule.consecutiveFailures ?? 0) > 0 ? (
                    <>
                      <br />
                      <small>
                        {schedule.consecutiveFailures} {schedule.consecutiveFailures === 1 ? 'failure' : 'failures'} in a row
                      </small>
                    </>
                  ) : null}
                </Table.Td>
                <Table.Td className="app-table-actions-column app-table-actions-column--wide">
                  <div className="app-table-primary-actions">
                    <Button small secondary disabled={isBusy} onClick={() => onRunNow(schedule)}>
                      {isBusy ? 'Running...' : 'Run now'}
                    </Button>
                    <RowActionsMenu
                      disabled={isBusy}
                      disabledReason="Wait for the current schedule action to finish"
                      label={`schedule ${schedule.name}`}
                      items={[
                        { icon: 'semantic-edit', label: 'Edit', onSelect: () => onEdit(schedule) },
                        {
                          icon: schedule.enabled ? 'semantic-pause' : 'semantic-play',
                          label: schedule.enabled ? 'Pause' : 'Resume',
                          onSelect: () => onToggleEnabled(schedule),
                        },
                        { icon: 'semantic-list', label: 'History', onSelect: () => onShowRuns(schedule) },
                        { danger: true, icon: 'semantic-delete', label: 'Delete', onSelect: () => onDelete(schedule) },
                      ]}
                      onDelete={() => undefined}
                      onEdit={() => undefined}
                    />
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </div>
  );
}
